import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import { Switch } from "@package/subframe/components/Switch";
import { TextArea } from "@package/subframe/components/TextArea";
import { FeatherArrowLeft } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { CheckSubmitFooter } from "@/components/checks/CheckSubmitFooter";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import {
  createFireExtinguisherCheckCommandControllerCreate,
  getListFireExtinguisherChecksQueryControllerListQueryKey,
  useCreateFireExtinguisherCheckCommandControllerCreate,
} from "@/generated/fire-extinguisher-checks/fire-extinguisher-checks";
import { useListFireExtinguishersQueryControllerList } from "@/generated/fire-extinguishers/fire-extinguishers";
import type { FireExtinguisherResponseDto } from "@/generated/model";
import {
  FIRE_EXTINGUISHER_CHECKS,
  type FireExtinguisherCheckKey,
} from "@/lib/fire-extinguisher-checks";
import { invalidateMonitoringQueries } from "@/lib/monitoring/invalidate-monitoring-queries";
import { canRaiseFollowUp } from "@/lib/roles";

export const Route = createFileRoute(
  "/_authenticated/fire-extinguisher-checks/new",
)({
  component: NewFireExtinguisherCheckPage,
});

type ItemState = Record<FireExtinguisherCheckKey, boolean> & {
  comment: string;
};

function freshItem(): ItemState {
  return {
    locationOk: true,
    signageOk: true,
    pressureOk: true,
    conditionOk: true,
    sealsOk: true,
    hardwareOk: true,
    comment: "",
  };
}

function NewFireExtinguisherCheckPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isPlatformAdmin, userRoleType } = useAuth();
  const { selectedNurseryId, nurseryRole } = useNursery();
  // Only managers and group admins may create follow-ups, so everyone else
  // gets plain Submit rather than a button that saves and then 403s.
  const canRaise = canRaiseFollowUp({
    isPlatformAdmin,
    userRoleType,
    nurseryRole,
  });

  const { data, isLoading } = useListFireExtinguishersQueryControllerList(
    selectedNurseryId ?? "",
    {},
    { query: { enabled: !!selectedNurseryId } },
  );
  const extinguishers = useMemo(() => data?.data ?? [], [data?.data]);

  const [state, setState] = useState<Record<string, ItemState>>({});

  const itemFor = (id: string): ItemState => state[id] ?? freshItem();

  const setCheck = (
    id: string,
    key: FireExtinguisherCheckKey,
    value: boolean,
  ) =>
    setState((prev) => ({
      ...prev,
      [id]: { ...itemFor(id), [key]: value },
    }));

  const setComment = (id: string, comment: string) =>
    setState((prev) => ({
      ...prev,
      [id]: { ...itemFor(id), comment },
    }));

  // Group extinguishers by room name for display, preserving the list order.
  const byRoom = useMemo(() => {
    const groups = new Map<string, FireExtinguisherResponseDto[]>();
    for (const e of extinguishers) {
      const list = groups.get(e.room.name) ?? [];
      list.push(e);
      groups.set(e.room.name, list);
    }
    return Array.from(groups.entries());
  }, [extinguishers]);

  const goBack = () => navigate({ to: "/fire-extinguisher-checks" });

  const {
    mutate: submit,
    isPending,
    isSuccess,
  } = useCreateFireExtinguisherCheckCommandControllerCreate({
    mutation: {
      onError: (error: unknown) => {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to submit check — please try again";
        toast.error(message);
      },
    },
  });

  // isSuccess keeps the form locked between the 201 and the navigation (the
  // awaited list refetch) so a second click can't create a duplicate check.
  const isSubmitting = isPending || isSuccess;

  const hasFailure = extinguishers.some((e) =>
    FIRE_EXTINGUISHER_CHECKS.some((check) => !itemFor(e.id)[check.key]),
  );

  const afterSubmit = async (
    response: Awaited<
      ReturnType<typeof createFireExtinguisherCheckCommandControllerCreate>
    >,
    raiseFollowUp: boolean,
  ) => {
    toast.success("Fire extinguisher check submitted");
    // The list is inactive while we're on this form, and the app sets
    // refetchOnMount: false — "all" forces the refetch so the new
    // check is visible on the history page. Await it BEFORE navigating:
    // mounting the history page mid-fetch lets the observer churn
    // (e.g. StrictMode's double-mount) cancel the in-flight refetch,
    // leaving the stale list.
    await queryClient.invalidateQueries({
      queryKey: getListFireExtinguisherChecksQueryControllerListQueryKey(
        selectedNurseryId ?? "",
      ),
      refetchType: "all",
    });
    void invalidateMonitoringQueries(queryClient);
    // AC17: go straight to the follow-up form when exactly one unit failed.
    // Zero or several means there is no single item to bind to, so open the
    // check, where each failed unit offers its own Raise follow-up.
    if (raiseFollowUp) {
      const failed = (response.data.items ?? []).filter((i) => !i.passed);
      const only = failed.length === 1 ? failed[0] : undefined;
      if (only) {
        const unit = extinguishers.find(
          (e) => e.id === only.fireExtinguisherId,
        );
        navigate({
          to: "/follow-up-actions/create",
          search: {
            nurseryId: selectedNurseryId ?? "",
            scheduledQuestionnaireId: "",
            answerId: "",
            questionText: "",
            checklistName: "",
            fireExtinguisherCheckId: response.data.id,
            fireExtinguisherCheckItemId: only.id,
            extinguisherName: unit?.name ?? "",
            fromSubmission: true,
          },
          // Replace the submitted form, so going back cannot reopen it blank.
          replace: true,
        });
        return;
      }
      navigate({
        to: "/fire-extinguisher-checks/$checkId",
        params: { checkId: response.data.id },
        replace: true,
      });
      return;
    }
    navigate({ to: "/fire-extinguisher-checks", replace: true });
  };

  const handleSubmit = (raiseFollowUp = false) => {
    if (isSubmitting) return;
    if (!selectedNurseryId || extinguishers.length === 0) return;
    submit(
      {
        nurseryId: selectedNurseryId,
        data: {
          items: extinguishers.map((e) => {
            const item = itemFor(e.id);
            return {
              fireExtinguisherId: e.id,
              locationOk: item.locationOk,
              signageOk: item.signageOk,
              pressureOk: item.pressureOk,
              conditionOk: item.conditionOk,
              sealsOk: item.sealsOk,
              hardwareOk: item.hardwareOk,
              comment: item.comment.trim() || undefined,
            };
          }),
        },
      },
      // Passed with the call rather than held in state, so the choice reaches
      // the success handler of this submission and no other.
      { onSuccess: (response) => afterSubmit(response, raiseFollowUp) },
    );
  };

  return (
    <div className="flex w-full max-w-2xl flex-col items-start gap-6 bg-white animate-fade-in">
      <div className="flex w-full items-start gap-2">
        <IconButton
          aria-label="Back"
          icon={<FeatherArrowLeft />}
          onClick={goBack}
        />
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
          <span className="w-full whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
            Fire Extinguisher Visual Checks
          </span>
          <span className="text-body font-body text-subtext-color">
            Confirm each extinguisher passes the six visual checks
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Loader size="large" />
        </div>
      ) : extinguishers.length === 0 ? (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 p-6">
          <span className="text-body font-body text-default-font">
            No fire extinguishers are registered for this nursery yet. Ask a
            group admin to add them in the nursery&apos;s settings.
          </span>
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col items-start gap-6">
            {byRoom.map(([roomName, units]) => (
              <div
                key={roomName}
                className="flex w-full flex-col items-start gap-3"
              >
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {roomName}
                </span>
                {units.map((unit) => {
                  const item = itemFor(unit.id);
                  return (
                    <div
                      key={unit.id}
                      className="flex w-full flex-col items-start gap-3 rounded-lg border border-neutral-200 p-4"
                    >
                      <span className="text-body-bold font-body-bold text-default-font">
                        {unit.name}
                      </span>
                      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
                        {FIRE_EXTINGUISHER_CHECKS.map((check) => (
                          <label
                            key={check.key}
                            className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5"
                          >
                            <span className="text-body font-body text-default-font">
                              {check.label}
                            </span>
                            <Switch
                              checked={item[check.key]}
                              onCheckedChange={(checked) =>
                                setCheck(unit.id, check.key, checked)
                              }
                              disabled={isSubmitting}
                            />
                          </label>
                        ))}
                      </div>
                      <TextArea
                        className="h-auto w-full"
                        label="Comment (optional)"
                      >
                        <TextArea.Input
                          placeholder="Note anything that needs attention"
                          value={item.comment}
                          onChange={(e) => setComment(unit.id, e.target.value)}
                          disabled={isSubmitting}
                          maxLength={4000}
                        />
                      </TextArea>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <CheckSubmitFooter
            onCancel={goBack}
            onSubmit={() => handleSubmit(false)}
            onSubmitAndRaiseFollowUp={
              canRaise ? () => handleSubmit(true) : undefined
            }
            isSubmitting={isSubmitting}
            canSubmit={extinguishers.length > 0}
            hasFailure={hasFailure}
          />
        </>
      )}
    </div>
  );
}
